import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaCoins } from 'react-icons/fa';
import SiteHeader from '../components/SiteHeader.jsx';
import SiteFooter from '../components/SiteFooter.jsx';
import CategoryCard from '../components/CategoryCard.jsx';
import SpinWheel from '../components/SpinWheel.jsx';
import { CATEGORIES } from '../data/categories.js';
import { useSeo } from '../hooks/useSeo.js';

const STEPS = [
  { icon: '🎯', gradient: 'from-violet-500 to-fuchsia-500', title: 'Pick a Quiz', body: 'Choose from 100+ free categories — GK, sports, science and more.' },
  { icon: '⚡', gradient: 'from-amber-500 to-orange-600', title: 'Answer Fast', body: 'Beat the clock and answer multiple-choice questions correctly.' },
  { icon: <FaCoins />, gradient: 'from-emerald-500 to-teal-600', title: 'Earn Coins', body: 'Collect virtual coins for every right answer — just for fun.' },
];

const FEATURES = [
  { icon: '🧠', gradient: 'from-amber-500 to-orange-600', title: '120+ Categories', body: 'A huge range of family-friendly trivia topics to explore.' },
  { icon: '🆓', gradient: 'from-emerald-500 to-teal-600', title: 'Always Free', body: 'Every quiz is free to play — no purchase, no entry fee, ever.' },
  { icon: '🔥', gradient: 'from-red-500 to-rose-600', title: 'Beat Your Score', body: 'Earn coins for correct answers and try to top your own best.' },
  { icon: '📱', gradient: 'from-sky-500 to-blue-600', title: 'Plays Everywhere', body: 'A smooth experience on mobile, tablet and desktop.' },
];

const FAQS = [
  { q: 'Is QuizzyLoot free to play?', a: 'Yes — every quiz is completely free, with no entry fees and no purchases required.' },
  { q: 'What are coins?', a: 'Coins are a virtual, in-game score you earn by answering correctly. They have no real-world cash value and cannot be exchanged for money or prizes.' },
  { q: 'Is this a contest or do I win prizes?', a: 'No. QuizzyLoot is a for-fun trivia game — there are no real prizes, winners, or cash rewards. You simply play to test your knowledge and beat your own score.' },
  { q: 'Do I need to create an account?', a: 'No account is needed to start playing — your coin score is saved on your device.' },
];

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="surface-card rounded-2xl">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left"
      >
        <span className="font-bold text-slate-900">{q}</span>
        <span className="text-brand text-xl shrink-0">{open ? '−' : '+'}</span>
      </button>
      {open && <p className="px-5 pb-5 -mt-1 text-sm text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function HomePage() {
  useSeo(
    'QuizzyLoot — Free Trivia Quiz Game (100+ Categories)',
    'Play 100+ free trivia quizzes across science, history, sports, movies and more. Answer questions and earn virtual coins — free to play, just for fun.',
  );
  const [spinOpen, setSpinOpen] = useState(false);
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const filteredCategories = q
    ? CATEGORIES.filter((c) => c.title.toLowerCase().includes(q))
    : CATEGORIES;

  return (
    <div className="min-h-screen flex flex-col page-in">
      <SiteHeader variant="light" />

      <main className="flex-1">
        {/* Category grid */}
        <section id="categories" className="mx-auto max-w-site px-4 sm:px-6 pt-8 pb-12">
          <div className="text-center mb-6 reveal">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900">
              Choose Your <span className="text-gradient-brand">Quiz</span>
            </h2>
            <p className="mt-3 text-slate-500">
              {CATEGORIES.length}+ free quizzes across every topic you love.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-md mx-auto mb-7">
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search categories…"
                className="w-full rounded-2xl bg-white border border-slate-200 shadow-sm pl-11 pr-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>

          {filteredCategories.length === 0 ? (
            <p className="text-center text-slate-400 py-10">No categories match “{query}”.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {filteredCategories.map((cat) => (
                <CategoryCard key={cat.id} cat={cat} />
              ))}
            </div>
          )}

          <p className="mt-7 text-center text-[11px] text-slate-500 max-w-xl mx-auto">
            🪙 Coins are a virtual in-game currency with no real-world cash value.
            QuizzyLoot is a free trivia game for entertainment — not a contest,
            lottery, or gambling service.
          </p>
        </section>

        {/* Mini-Game Hub banner */}
        <section className="mx-auto max-w-site px-4 sm:px-6 pb-2">
          <Link
            to="/minigames"
            className="reveal-fade relative overflow-hidden block surface-purple rounded-[2rem] px-6 sm:px-10 py-7 ring-1 ring-white/15 flex flex-col sm:flex-row items-center gap-5 sm:gap-8 transition-transform duration-200 hover:-translate-y-1"
          >
            <span className="absolute inset-0 halftone opacity-20 pointer-events-none" />
            <span className="absolute inset-0 diag-shine pointer-events-none" />
            <div className="relative text-5xl sm:text-6xl shrink-0">🎮</div>
            <div className="relative flex-1 text-center sm:text-left">
              <h2 className="text-2xl sm:text-3xl font-black text-white">
                Play 10 Mini-Games
              </h2>
              <p className="mt-1 text-sm text-white/85">
                Tap, whack, build &amp; pop — earn coins, revive and double your rewards.
              </p>
            </div>
            <span className="relative btn-3d inline-flex items-center gap-2 rounded-2xl bg-amber-brand px-6 py-3 text-sm font-black uppercase tracking-wide text-slate-900 shrink-0">
              Open Hub →
            </span>
          </Link>
        </section>

        {/* How it works */}
        <section>
          <div className="mx-auto max-w-site px-4 sm:px-6 py-10">
            <div className="text-center mb-8 reveal">
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900">How It Works</h2>
              <p className="mt-2 text-slate-500">Three simple steps to start playing.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
              {STEPS.map((s, i) => (
                <div
                  key={s.title}
                  style={{ animationDelay: `${i * 90}ms` }}
                  className="reveal-fade relative surface-card rounded-3xl p-7 text-center transition-transform duration-200 hover:-translate-y-1"
                >
                  <span className="absolute top-4 right-5 text-4xl font-black text-violet-100">
                    {i + 1}
                  </span>
                  <div
                    className={`relative overflow-hidden mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br ${s.gradient} flex items-center justify-center text-3xl text-white shadow-lg mb-4`}
                  >
                    <span className="absolute inset-0 halftone opacity-25" />
                    <span className="absolute inset-0 diag-shine" />
                    <span className="relative">{s.icon}</span>
                  </div>
                  <h3 className="text-lg font-black text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="mx-auto max-w-site px-4 sm:px-6 py-10">
          <div className="text-center mb-8 reveal">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-900">
              Why <span className="text-gradient-brand">QuizzyLoot</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                style={{ animationDelay: `${i * 80}ms` }}
                className="reveal-fade surface-card rounded-3xl p-6 transition-transform duration-200 hover:-translate-y-1"
              >
                <div
                  className={`relative overflow-hidden w-14 h-14 rounded-2xl bg-gradient-to-br ${f.gradient} flex items-center justify-center text-2xl text-white shadow-lg mb-4`}
                >
                  <span className="absolute inset-0 halftone opacity-25" />
                  <span className="absolute inset-0 diag-shine" />
                  <span className="relative">{f.icon}</span>
                </div>
                <h3 className="font-black text-slate-900">{f.title}</h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Play CTA banner */}
        <section className="mx-auto max-w-site px-4 sm:px-6 pb-10">
          <div className="reveal relative overflow-hidden hero-gradient rounded-[2.5rem] px-6 sm:px-12 py-10 text-center text-white ring-1 ring-white/15">
            <span className="absolute inset-0 halftone opacity-20 pointer-events-none" />
            <span className="absolute inset-0 diag-shine pointer-events-none" />
            <h2 className="relative text-3xl sm:text-4xl font-black">Ready to Play? 🎯</h2>
            <p className="relative mt-2 text-white/85 max-w-lg mx-auto">
              Jump into a free trivia quiz and earn virtual coins — just for fun.
            </p>
            <Link
              to="/play"
              className="btn-3d relative mt-6 inline-flex rounded-2xl bg-white px-7 py-3.5 text-base font-black uppercase tracking-wide text-brand"
            >
              Start Playing
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section>
          <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
            <div className="text-center mb-6 reveal">
              <h2 className="text-3xl sm:text-4xl font-black text-slate-900">FAQ</h2>
            </div>
            <div className="space-y-3 reveal">
              {FAQS.map((f) => (
                <Faq key={f.q} q={f.q} a={f.a} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />

      {/* Floating action group: Get the App + Spin */}
      <div className="fixed bottom-5 right-5 z-30 flex items-center gap-3">
        {/* Google Play download */}
        <a
          href="https://play.google.com/store/apps/details?id=com.quizzyloot.android.online.global"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-3d flex items-center gap-2 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white pl-3 pr-4 py-2.5"
        >
          <img src="/assets/gplay.svg" alt="" className="w-6 h-6" />
          <span className="leading-none text-left">
            <span className="block text-[8px] font-bold uppercase tracking-wide text-slate-300">
              Get it on
            </span>
            <span className="block text-sm font-black">Google Play</span>
          </span>
        </a>

        {/* Spin — opens the daily spin wheel */}
        <button
          onClick={() => setSpinOpen(true)}
          className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 shadow-[0_10px_30px_rgba(251,146,60,0.5)] flex flex-col items-center justify-center text-white border-4 border-white/40 z-30"
        >
          <FaCoins className="coin-flip text-xl drop-shadow" />
          <span className="text-[8px] font-black uppercase mt-0.5">Spin</span>
        </button>
      </div>

      {spinOpen && <SpinWheel onClose={() => setSpinOpen(false)} />}
    </div>
  );
}
